import { z } from "zod";
import { LessonStatus } from "./room.ts";
import { MarkedOccurrence } from "./student-profile.ts";

/**
 * Занятия пары на карточке ученика: ОДИН список вместо двух (решение владельца
 * 26.09.2026).
 *
 * До этого дня карточка держала два списка про одно и то же: «Занятия» из
 * УРОКОВ пары и «Учёт» из ЗАНЯТИЙ расписания за полгода. Правильная единица
 * это занятие, а не урок: у преподавателя, ведущего уроки в Zoom (режим
 * сосуществования, раздел 2 CLAUDE.md, штатная конфигурация продукта), уроков
 * нет вовсе, а занятия есть. Урок здесь это необязательное приложение к
 * занятию, если оно им стало.
 *
 * ⚠️ **Ни одно поле этого файла не уезжает ученику**, по той же причине, что у
 * соседнего `student-profile.ts`: отметки посещений и оплат это записи
 * преподавателя о паре. Реэкспорт из корня безопасен ровно так же, как там:
 * секрет это данные, а не форма JSON.
 */

/**
 * Урок, которым стало занятие. Ровно те поля, из которых строка решает своё
 * действие («Войти», «Архив», «открывать нечего») и ссылку «задать домашнюю».
 * Не `Lesson` целиком: у того двадцать полей под другие экраны, и половина их
 * здесь была бы весом на проводе при 128 кбит/с.
 */
export const PairOccurrenceLesson = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: LessonStatus,
  hasBoardSnapshot: z.boolean(),
  /** Заготовка урока. `null` и когда её не было, и когда её стёрли. */
  planId: z.string().uuid().nullable(),
});
export type PairOccurrenceLesson = z.infer<typeof PairOccurrenceLesson>;

/**
 * Строка списка: занятие с его отметками плюс урок, если занятие им стало.
 *
 * `title` унаследован от `MarkedOccurrence` и равен названию урока; поле
 * оставлено, а не выведено из `lesson`, чтобы строка оставалась тем же
 * `MarkedOccurrence`, который понимают счётчики и `countUnpaid`.
 *
 * ⚠️ При выключенном учёте `paidAt` и `attendance` всегда `null`: таблица
 * отметок в этом случае не читается вовсе (раздел 5.10 CLAUDE.md, «ни одного
 * запроса ради учёта»), и `null` здесь значит «не спрашивали», а не «не
 * отмечено».
 */
export const PairOccurrence = MarkedOccurrence.extend({
  lesson: PairOccurrenceLesson.nullable(),
  /**
   * Строка ВНЕ учёта: она есть в списке, но счётчик «не отмечено» и фильтр
   * «Неоплаченные» её не видят, и чипов «Был» и «Оплата» у неё нет. Иначе
   * кнопка «отметить», которую не видит ни один счётчик, врала бы про долг.
   * Таких строк два вида, и оба существуют ради одного: не потерять с карточки
   * урок с доской и архивом.
   *
   * 1. осиротевший урок со слотом: его занятия раскрытие расписания больше не
   *    даёт (серию поправили, занятие отменили), а урок остался;
   * 2. урок, завершённый раньше своего момента (подготовили к завтра, зашли и
   *    нажали «Завершить»): в будущее он уже не годится, а в историю встаёт по
   *    `ended_at`.
   */
  unaccounted: z.boolean(),
});
export type PairOccurrence = z.infer<typeof PairOccurrence>;

/**
 * Фильтр списка. Сегодня один: «Неоплаченные».
 *
 * ⚠️ Параметр ручки, а не отбор в браузере: браузер видит только загруженные
 * страницы, и фильтр поверх них врал бы при пагинации («неоплаченных нет» на
 * первых десяти строках при семи неоплаченных дальше).
 */
export const PairOccurrenceFilter = z.enum(["unpaid"]);
export type PairOccurrenceFilter = z.infer<typeof PairOccurrenceFilter>;

/**
 * Сколько будущих уроков показывать над историей. Берутся ближайшие: дальше
 * десятка это уже работа расписания, а не карточки.
 */
export const PAIR_OCCURRENCE_UPCOMING_LIMIT = 10;

/**
 * Страница истории, ответ `GET /v1/students/:userId/occurrences`.
 * Размеры страниц общие со старым списком уроков
 * (`STUDENT_LESSON_PAGE_LIMIT_DEFAULT`, `STUDENT_LESSON_MORE_LIMIT`,
 * `STUDENT_LESSON_PAGE_LIMIT_MAX`): смысл у них тот же, а экран один.
 */
export const PairOccurrencesResponse = z.object({
  rows: z.array(PairOccurrence),
  /** `null` значит «дальше ничего нет». Курсор непрозрачный. */
  nextCursor: z.string().nullable(),
});
export type PairOccurrencesResponse = z.infer<typeof PairOccurrencesResponse>;

/** Первая страница в самой карточке (`StudentDetail.occurrences`). */
export const StudentOccurrences = PairOccurrencesResponse.extend({
  /** Занятий в истории всего, без фильтра: число в заголовке. */
  count: z.number().int(),
  /**
   * Созданные, не завершённые и ещё не наступившие уроки пары: десять
   * БЛИЖАЙШИХ, по убыванию момента (дальнее сверху), чтобы столбец дат шёл
   * монотонно вместе с историей под ним. Только уроки: незаведённые будущие
   * занятия это работа расписания.
   */
  upcoming: z.array(PairOccurrence),
});
export type StudentOccurrences = z.infer<typeof StudentOccurrences>;
